import type {RumGlobal} from '@datadog/browser-rum';
import React, {
  Profiler as ReactProfiler,
  type ProfilerOnRenderCallback,
  type ReactNode,
  useCallback,
} from 'react';

import {useDatadogRum} from '../../datadog';

type OnRenderParams = Parameters<ProfilerOnRenderCallback>;

// This function uses DD "custom vitals" to track render durations. React
// doesn't give us start and end render events, it just gives us a retrospective
// duration. That's fine, RUM can do retrospective vitals by providng a start
// time and a duration, and it figures out what to do with that info.
function logToDatadogRum(datadog: RumGlobal, ...props: OnRenderParams) {
  const [id, phase, actualDuration, baseDuration, startTime] = props;

  datadog.addDurationVital(id, {
    startTime,
    duration: actualDuration,
    description: phase,
  });

  // The `baseDuration` property is React's estimate of the render time in the
  // case where all the memoization is invalidated, and it has to do the most-
  // expensive render it can. In principle, the ratio between actual duration
  // and worstcase duration should be a decent proxy for "cache hit percentage"
  datadog.addDurationVital(`${id}__worstcase`, {
    startTime,
    duration: baseDuration,
    description: phase,
  });
}

interface ProfilerProps {
  children: ReactNode;
  id: string;
  onRender?: ProfilerOnRenderCallback;
}

/**
 * This component allows profiling the render performance of a particular
 * subtree of React components. It automatically forwards perf information to
 * datadog via the "Custom Vitals" feature.
 *
 * It also behaves as a standard React `Profiler` component, allowing you the
 * user to optionally do what they want with the performance information.
 *
 * @see <https://docs.datadoghq.com/real_user_monitoring/browser/monitoring_page_performance/#measure-component-level-performance-with-custom-vitals>
 * @see <https://react.dev/reference/react/Profiler>
 */
export function Profiler({children, id, onRender}: ProfilerProps) {
  const datadogRum = useDatadogRum();

  const onRenderInternal = useCallback(
    (...args: OnRenderParams) => {
      if (onRender) {
        onRender(...args);
      }

      // Profilers bubble up, so nested profilers log to parent profilers,
      // allowing parents to multiplex. We don't want that for DD logging, so
      // only send metrics if it's _this_ profiler instance that's reporting
      // render performance.
      const [renderId] = args;
      if (renderId === id) {
        logToDatadogRum(datadogRum, ...args);
      }
    },
    [datadogRum, id, onRender]
  );

  return (
    <ReactProfiler id={id} onRender={onRenderInternal}>
      {children}
    </ReactProfiler>
  );
}
